import React from 'react';
import { decimalsToWei } from '@akropolis-web/primitives';
import { ChartsAxisContentProps } from '@mui/x-charts';

import { colors, styled } from 'core/styles';
import { getAmount } from 'domain/utils';
import { FormattedAmount } from 'components';
import { schemas } from 'api/modules/apostro-rest/types';

type LiqLevelsTooltipProps = {
  data: ChartsAxisContentProps;
  marketName: string;
  liqType: schemas['LiqType'];
};

export function LiqLevelsTooltip({ data, marketName, liqType }: LiqLevelsTooltipProps) {
  const { axisValue: price, dataIndex, series } = data;
  const [other, market] = series;

  if (price === null || typeof dataIndex !== 'number') {
    return null;
  }

  const marketLiqVolume = {
    value: (market.data[dataIndex] as number) ?? null,
    color: market.color || '#FFFFFF50',
  };

  const othersLiqVolume = {
    value: (other.data[dataIndex] as number) ?? null,
    color: other.color || '#FFFFFF50',
  };

  return (
    <Root>
      <TooltipContent>
        <span>
          <ColorMark color="#00000000" />
          Liquidation price:{' '}
          <FormattedAmount
            sum={getAmount(Number(price), '$').mul(decimalsToWei(18))}
            precision={2}
          />
        </span>
        <span>
          <ColorMark color={othersLiqVolume.color} />
          All money market&apos;s {liqType.toLowerCase()}:{' '}
          {othersLiqVolume.value !== null && marketLiqVolume.value !== null ? (
            <FormattedAmount
              sum={getAmount(othersLiqVolume.value + marketLiqVolume.value, '$').mul(
                decimalsToWei(18),
              )}
              precision={2}
              withSI
            />
          ) : (
            'N/A'
          )}
        </span>
        <span>
          <ColorMark color={marketLiqVolume.color} />
          {marketName}&apos;s {liqType.toLowerCase()}:{' '}
          {marketLiqVolume.value !== null ? (
            <FormattedAmount
              sum={getAmount(marketLiqVolume.value, '$').mul(decimalsToWei(18))}
              precision={2}
              withSI
            />
          ) : (
            'N/A'
          )}
        </span>
      </TooltipContent>
    </Root>
  );
}

const Root = styled('div')(() => ({
  padding: '4px 8px 6px 8px',
  background: colors.smokyWhite,
  borderRadius: 4,
  fontSize: 12,
}));

const TooltipContent = styled('p')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}));

const ColorMark = styled('span', {
  shouldForwardProp: prop => prop !== 'color',
})<{
  color: string;
}>(({ color }) => ({
  display: 'inline-block',
  width: 8,
  height: 8,
  background: color,
  marginRight: 6,
}));
